import React, { useEffect, useState, useRef } from 'react'
import ReactPlayer from 'react-player/lazy'
import type { ComposeComponentYoutubeVideoFragment } from '../../../.graphql/generated-types'

export interface YoutubeVideoProps {
  data: ComposeComponentYoutubeVideoFragment
}
export function YoutubeVideo({ data }: YoutubeVideoProps) {
  const [videoObserver, setVideoObserver] =
    useState<IntersectionObserver | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const container = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    // Mount the observer that will fire whether the video is in the viewport
    if (!videoObserver) {
      const observer = new IntersectionObserver(
        buildOnVideoIntersectionCallback(setIsVisible),
        {
          rootMargin: '100px 0px',
          threshold: 0.25,
        },
      )
      setVideoObserver(observer)
      return () => {
        observer.disconnect()
      }
    }
    return () => undefined
  }, [videoObserver])
  useEffect(() => {
    // We attach an intersection observer to the underlying div to ensure
    // when it enters the viewport, the callback is triggered to load the video
    if ('IntersectionObserver' in window) {
      if (videoObserver && container.current) {
        videoObserver.observe(container.current)
      }
    } else {
      setIsVisible(true)
    }
  }, [container, videoObserver])
  return (
    <div>
      <div ref={container} className="flex w-full">
        {isVisible && data.url && (
          <ReactPlayer
            url={data.url}
            controls={true}
            width="100%"
            className="aspect-auto"
          />
        )}
      </div>
    </div>
  )
}

/**
 * Build callback for video entering viewport.
 * Needed to capture the `setIsVisible` state setter into the callback.
 */
function buildOnVideoIntersectionCallback(
  setIsVisible: (isVisible: boolean) => void,
) {
  const callBack = (
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver,
  ) => {
    onVideoIntersection(setIsVisible, entries, observer)
  }
  return callBack
}

/**
 * Callback fired when video enters viewport:
 * Sets state to show vide and unattaches observer.
 */
function onVideoIntersection(
  setShowVideo: (isVisible: boolean) => void,
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
) {
  if (entries[0]?.isIntersecting) {
    setShowVideo(true)
    observer.disconnect()
  }
}
